/**
 * Módulo de Consulta Pública de Datos Epidemiológicos (Dashboard Consult).
 * Proporciona una interfaz abierta para visualizar estadísticas agregadas por municipio,
 * niveles de riesgo territorial y un mapa interactivo de casos detectados.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Apiurl } from "../../shared/services/apirest";
import LogoGeo from "../../assets/img/LogoGeo.png";
import LogosFooter from "../../shared/components/LogosFooter";
import "../../assets/css/DashboardConsult.css";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const DashboardConsult = () => {
    const [casos, setCasos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const [showFilters, setShowFilters] = useState(true); // Filtros visibles por defecto
    const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);

    // Estado inicial de los filtros de búsqueda
    const [filters, setFilters] = useState({
        timeRange: "all", // 7d, 30d, 3m, 1y, custom, all
        customStart: "",
        customEnd: "",
        municipio: "",
        riesgo: "",
        parasite: "", // Ascaris, Trichuris, Uncinarias
        status: "" // Activos, Inactivos
    });

    /**
     * Obtiene todos los casos públicos desde la API.
     */
    useEffect(() => {
        const fetchCasos = async () => {
            try {
                const response = await axios.get(`${Apiurl}casos/`);
                setCasos(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCasos();
    }, []);

    /**
     * Auxiliar: Verifica si una fecha está dentro del rango seleccionado.
     */
    const isWithinRange = (dateStr, range, start, end) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = new Date();

        // Ajustar hora para comparación limpia
        now.setHours(23, 59, 59, 999);

        switch (range) {
            case "7d": return date >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            case "30d": return date >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            case "3m": return date >= new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            case "1y": return date >= new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            case "custom":
                if (start && new Date(dateStr) < new Date(start)) return false;
                if (end && new Date(dateStr) > new Date(end)) return false;
                return true;
            default: return true; // 'all'
        }
    };

    /**
     * Agregación de datos por Municipio.
     * Calcula estadísticas filtradas para la tabla y el mapa.
     */
    const municipalityStats = useMemo(() => {
        const stats = {};

        // 1. Aplicar filtros a la data cruda
        const filtered = casos.filter(c => {
            // Filtro por Municipio
            if (filters.municipio && c.paciente_municipio !== filters.municipio) return false;

            // Filtro por Riesgo
            if (filters.riesgo && (c.nivel_peligrosidad || "BAJO").toUpperCase() !== filters.riesgo) return false;

            // Filtro por Rango de Tiempo
            if (!isWithinRange(c.fecha_registro, filters.timeRange, filters.customStart, filters.customEnd)) return false;

            // Filtro por Parásito Detectado
            if (filters.parasite) {
                const pMap = {
                    "Ascaris lumbricoides": "Ascaris",
                    "Trichuris trichiura": "Trichuris",
                    "Ancylostoma duodenale": "Uncinarias"
                };
                const target = pMap[filters.parasite] || filters.parasite;

                if (target !== "Todos" && (!c.parasitos_detectados || !c.parasitos_detectados.includes(target))) {
                    return false;
                }
            }

            // Filtro por Estado (Vivo/Activo en los últimos 30 días)
            if (filters.status) {
                const date = new Date(c.fecha_registro);
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                const isActive = date >= thirtyDaysAgo;

                if (filters.status === "Activos" && !isActive) return false;
                if (filters.status === "Inactivos" && isActive) return false;
            }

            return true;
        });

        // 2. Agrupar por municipio
        filtered.forEach(c => {
            const muni = c.paciente_municipio || "Sin Definir";
            if (!stats[muni]) {
                stats[muni] = {
                    municipio: muni,
                    total: 0,
                    active: 0,
                    risks: { ALTO: 0, MEDIO: 0, BAJO: 0 },
                    lat: c.latitud, // Captura primera coordenada encontrada para el punto en el mapa
                    lng: c.longitud
                };
            }
            // Fallback para coordenadas
            if (!stats[muni].lat && c.latitud) {
                stats[muni].lat = c.latitud;
                stats[muni].lng = c.longitud;
            }

            stats[muni].total += 1;

            // Casos Activos (Lógica de los últimos 30 días)
            const date = new Date(c.fecha_registro);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            if (date >= thirtyDaysAgo) {
                stats[muni].active += 1;
            }

            // Contador de Riesgos
            const risk = (c.nivel_peligrosidad || "BAJO").toUpperCase();
            if (stats[muni].risks[risk] !== undefined) {
                stats[muni].risks[risk] += 1;
            } else {
                stats[muni].risks["BAJO"] += 1;
            }
        });

        // 3. Calcular Riesgo Predominante y convertir a array
        let result = Object.values(stats).map(item => {
            const { risks } = item;
            let predominant = "BAJO";
            let max = risks.BAJO;

            if (risks.MEDIO > max) {
                max = risks.MEDIO;
                predominant = "MEDIO";
            }
            if (risks.ALTO > max) {
                max = risks.ALTO;
                predominant = "ALTO";
            }
            // Prioridad en caso de empate
            if (risks.ALTO === max) predominant = "ALTO";
            else if (risks.MEDIO === max) predominant = "MEDIO";

            return { ...item, predominantRisk: predominant };
        });

        return result;
    }, [casos, filters]);

    /**
     * Maneja el cambio en cualquier campo de los filtros.
     */
    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const uniqueMunicipios = useMemo(() => [...new Set(casos.map(c => c.paciente_municipio).filter(Boolean))], [casos]);
    const parasiteOptions = ["Ascaris lumbricoides", "Trichuris trichiura", "Ancylostoma duodenale"];

    /**
     * Helper: Define colores según el nivel de riesgo.
     */
    const getRiskColor = (risk) => {
        switch (risk) {
            case "ALTO": return "#EF5350"; // Rojo
            case "MEDIO": return "#FFA726"; // Naranja
            case "BAJO": return "#66BB6A"; // Verde
            default: return "#66BB6A";
        }
    };

    return (
        <div className="dc-container">
            {/* Barra de Navegación Unificada */}
            <nav className="navbar navbar-expand-lg navbar-custom navbar-dark fixed-top shadow-sm">
                <div className="container-fluid">
                    <Link className="navbar-brand" to="/">
                        <img src={LogoGeo} alt="GeoHCX" className="nav-logo-img me-2" />
                        <span className="fw-bold text-white fs-4">GEOHCX</span>
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={handleNavCollapse}
                        aria-controls="navbarNav"
                        aria-expanded={!isNavCollapsed}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse justify-content-end`} id="navbarNav">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item"><Link className="nav-link" to="/" onClick={() => setIsNavCollapsed(true)}>INICIO</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/quienes-somos" onClick={() => setIsNavCollapsed(true)}>QUIENES SOMOS</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/indicadores" onClick={() => setIsNavCollapsed(true)}>INDICADORES</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/mapa" onClick={() => setIsNavCollapsed(true)}>UBICACIÓN</Link></li>
                            <li className="nav-item"><Link className="nav-link active" to="/consultar" onClick={() => setIsNavCollapsed(true)}>CONSULTAR</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/ambiental" onClick={() => setIsNavCollapsed(true)}>DATOS AMBIENTALES</Link></li>
                            <li className="nav-item ms-lg-3">
                                <Link to="/login" className="btn-login" onClick={() => setIsNavCollapsed(true)}>INICIAR SESIÓN</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            {/* Encabezado Principal */}
            <header className="dc-header-bg">
                <div className="container h-100 d-flex flex-column justify-content-center">
                    <h1 className="text-white fw-bold mb-2">Consulta General por Municipio</h1>
                    <p className="text-white opacity-75">Resumen epidemiológico y nivel de riesgo territorial</p>
                </div>
            </header>

            <div className="container mt-4 pb-5">

                {/* 1. Sección de Filtros */}
                <div className="dc-card mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <h5 className="dc-card-title m-0"><i className="bi bi-funnel"></i> Filtros de Búsqueda</h5>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowFilters(!showFilters)}>
                                {showFilters ? <><i className="bi bi-eye-slash"></i> Ocultar</> : <><i className="bi bi-eye"></i> Mostrar</>}
                            </button>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setFilters({ timeRange: "all", customStart: "", customEnd: "", municipio: "", riesgo: "", parasite: "", status: "" })}>
                            Limpiar Filtros
                        </button>
                    </div>

                    {showFilters && (
                        <div className="row g-2">
                            {/* Período de Tiempo */}
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small fw-bold text-secondary">Período de Tiempo</label>
                                <select className="form-select form-select-sm" name="timeRange" value={filters.timeRange} onChange={handleFilterChange}>
                                    <option value="all">Todo el Historial</option>
                                    <option value="7d">Últimos 7 días</option>
                                    <option value="30d">Últimos 30 días</option>
                                    <option value="3m">Últimos 3 meses</option>
                                    <option value="1y">Último año</option>
                                    <option value="custom">Rango Personalizado</option>
                                </select>
                            </div>

                            {/* Rango de Fecha Personalizado */}
                            {filters.timeRange === "custom" && (
                                <>
                                    <div className="col-12 col-md-6 col-lg-2">
                                        <label className="form-label small fw-bold text-secondary">Desde</label>
                                        <input type="date" className="form-control form-select-sm" name="customStart" value={filters.customStart} onChange={handleFilterChange} />
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-2">
                                        <label className="form-label small fw-bold text-secondary">Hasta</label>
                                        <input type="date" className="form-control form-select-sm" name="customEnd" value={filters.customEnd} onChange={handleFilterChange} />
                                    </div>
                                </>
                            )}

                            {/* Municipio */}
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small fw-bold text-secondary">Municipio</label>
                                <select className="form-select form-select-sm" name="municipio" value={filters.municipio} onChange={handleFilterChange}>
                                    <option value="">Todos los Municipios</option>
                                    {uniqueMunicipios.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>

                            {/* Nivel de Riesgo */}
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small fw-bold text-secondary">Nivel de Riesgo</label>
                                <select className="form-select form-select-sm" name="riesgo" value={filters.riesgo} onChange={handleFilterChange}>
                                    <option value="">Todos</option>
                                    <option value="ALTO">🔴 Alto (Rojo)</option>
                                    <option value="MEDIO">🟠 Medio (Naranja)</option>
                                    <option value="BAJO">🟢 Bajo (Verde)</option>
                                </select>
                            </div>

                            {/* Parásito */}
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small fw-bold text-secondary">Parásito Específico</label>
                                <select className="form-select form-select-sm" name="parasite" value={filters.parasite} onChange={handleFilterChange}>
                                    <option value="">Todos</option>
                                    {parasiteOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            {/* Estado del Caso */}
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small fw-bold text-secondary">Estado del Caso</label>
                                <select className="form-select form-select-sm" name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option value="">Todos</option>
                                    <option value="Activos">Activos (Últimos 30 días)</option>
                                    <option value="Inactivos">Inactivos / Históricos</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                <div className="row">
                    {/* 2. Tabla de Resultados (Columna Izquierda) */}
                    <div className="col-12 col-lg-6 mb-4">
                        <div className="dc-card h-100">
                            <h5 className="dc-card-title"><i className="bi bi-table"></i> Resumen por Municipio</h5>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Municipio</th>
                                            <th className="text-center">Total Casos</th>
                                            <th className="text-center">Casos Activos</th>
                                            <th className="text-center">Riesgo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="4" className="text-center py-4">Procesando datos...</td></tr>
                                        ) : municipalityStats.length > 0 ? (
                                            municipalityStats.map((item, i) => (
                                                <tr key={i}>
                                                    <td className="fw-bold">{item.municipio}</td>
                                                    <td className="text-center">{item.total}</td>
                                                    <td className="text-center text-primary fw-bold">{item.active}</td>
                                                    <td className="text-center">
                                                        <span className="badge" style={{ backgroundColor: getRiskColor(item.predominantRisk) }}>
                                                            {item.predominantRisk}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="4" className="text-center py-4 text-muted">No se encontraron resultados para los filtros seleccionados.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* 3. Mapa Interactivo (Columna Derecha) */}
                    <div className="col-12 col-lg-6 mb-4">
                        <div className="dc-card h-100 p-0 overflow-hidden">
                            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Mapa Territorial del Riesgo</h5>
                                <small className="text-muted"><i className="bi bi-cursor-fill"></i> Nodos territoriales</small>
                            </div>
                            <div style={{ height: "500px", width: "100%" }}>
                                <MapContainer center={[8.75, -75.88]} zoom={8} style={{ height: "100%", width: "100%" }}>
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    {municipalityStats.map((item, idx) => {
                                        if (!item.lat || !item.lng) return null;

                                        return (
                                            <CircleMarker
                                                key={idx}
                                                center={[item.lat, item.lng]}
                                                radius={10}
                                                pathOptions={{
                                                    color: "white",
                                                    fillColor: getRiskColor(item.predominantRisk),
                                                    fillOpacity: 0.8,
                                                    weight: 2
                                                }}
                                            >
                                                <LeafletTooltip direction="top" offset={[0, -10]} opacity={1}>
                                                    <div className="text-center">
                                                        <strong>{item.municipio}</strong><br />
                                                        <span className="badge bg-light text-dark border mt-1">
                                                            {item.total} casos registrados
                                                        </span>
                                                    </div>
                                                </LeafletTooltip>
                                            </CircleMarker>
                                        );
                                    })}
                                </MapContainer>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Logotipos Institucionales */}
            <LogosFooter />

            {/* Pie de Página */}
            <footer className="footer-site py-5 mt-5">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-3 text-center text-md-start">
                            <img src={LogoGeo} alt="Logo Footer" className="footer-logo-img" />
                        </div>
                        <div className="col-md-6">
                            <ul className="footer-nav d-flex justify-content-center mb-0">
                                <li><Link to="/">Inicio</Link></li>
                                <li><a href="#about">Acerca de GeoHCX</a></li>
                            </ul>
                        </div>
                        <div className="col-md-3 text-center text-md-end">
                            <p className="text-muted small mb-0">© 2025 GEOHCX - Vigilancia Epidemiológica Inteligente</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default DashboardConsult;
